import { useState } from "react";
import { ExternalLink, MoreVertical, RefreshCw, Trash2, Undo2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { store, useStore } from "@/lib/store";
import type { PlatformDeployment } from "@/lib/platformTypes";
import { cn } from "@/lib/utils";
import { EmptyState, ViewSection, relativeTime } from "@/views/shared";
import { ConfirmActionDialog } from "./ConfirmActionDialog";

function deploymentStatusBadge(status: string | null) {
  const s = status ?? "unknown";
  return (
    <Badge
      variant="outline"
      className={cn(
        "shrink-0 font-mono text-[10px] uppercase tracking-wider",
        s === "ready" && "border-primary/40 bg-primary/10 text-primary",
        s === "error" && "border-destructive/40 bg-destructive/10 text-destructive",
        (s === "building" || s === "pending") &&
          "animate-pulse border-border bg-secondary/50 text-muted-foreground",
        s !== "ready" && s !== "error" && s !== "building" && s !== "pending" &&
          "border-border text-muted-foreground",
      )}
    >
      {s}
    </Badge>
  );
}

interface PendingConfirm {
  kind: "rollback" | "delete";
  deployment: PlatformDeployment;
}

export function DeploymentList({ projectId }: { projectId: string }) {
  const state = useStore();
  const deployments: PlatformDeployment[] | undefined =
    state.platform.deploymentsByProject[projectId];
  const pendingAction = state.platform.pendingAction;
  const loadError = state.errors[`platform:deployments:${projectId}`];
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);

  const confirmPendingKey = confirm
    ? confirm.kind === "rollback"
      ? `rollback:${confirm.deployment.id}`
      : `delete-deployment:${confirm.deployment.id}`
    : null;
  const confirmPending = confirmPendingKey !== null && pendingAction === confirmPendingKey;

  async function runConfirm() {
    if (!confirm) return;
    try {
      if (confirm.kind === "rollback") {
        await store.rollbackDeployment(projectId, confirm.deployment.id);
      } else {
        await store.deleteDeploymentRecord(projectId, confirm.deployment.id);
      }
    } catch {
      // The store already records the failure under errors["platform:..."];
      // close so the inline error is visible instead of a stuck dialog.
    }
    setConfirm(null);
  }

  return (
    <ViewSection
      title="Deployments"
      action={
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          onClick={() => void store.loadDeployments(projectId)}
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </Button>
      }
    >
      {loadError && <p className="mb-2 text-sm text-destructive">{loadError}</p>}

      {deployments === undefined ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-xl" />
          ))}
        </div>
      ) : deployments.length === 0 ? (
        <EmptyState message="No deployments recorded for this project." />
      ) : (
        <div className="flex flex-col gap-2">
          {deployments.map((d) => {
            const rowError = state.errors[`platform:rollback:${d.id}`]
              ?? state.errors[`platform:delete-deployment:${d.id}`]
              ?? d.error;
            return (
              <div key={d.id} className="glass-border rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    {d.id.slice(0, 8)}
                  </span>
                  {deploymentStatusBadge(d.status)}
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                    {relativeTime(d.createdAt)}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground"
                        aria-label="Deployment actions"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="glass glass-border">
                      <DropdownMenuItem onSelect={() => setConfirm({ kind: "rollback", deployment: d })}>
                        <Undo2 className="mr-2 h-4 w-4" />
                        Rollback to this
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => setConfirm({ kind: "delete", deployment: d })}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete record
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {d.url && (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex min-w-0 max-w-full items-center gap-1 font-mono text-xs text-primary hover:underline"
                  >
                    <span className="truncate">{d.url.replace(/^https?:\/\//, "")}</span>
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                )}
                {rowError && <p className="mt-1 text-xs text-destructive">{rowError}</p>}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmActionDialog
        open={confirm !== null}
        onOpenChange={(open) => { if (!open) setConfirm(null); }}
        title={confirm?.kind === "rollback" ? "Rollback deployment" : "Delete deployment record"}
        description={
          confirm?.kind === "rollback"
            ? `Point the live site back at deployment ${confirm.deployment.id.slice(0, 8)}. Later deployments stay in the history.`
            : `Remove deployment record ${confirm ? confirm.deployment.id.slice(0, 8) : ""} from the project history. This does not affect the live site.`
        }
        actionLabel={confirm?.kind === "rollback" ? "Rollback" : "Delete record"}
        destructive={confirm?.kind === "delete"}
        pending={confirmPending}
        onConfirm={runConfirm}
      />
    </ViewSection>
  );
}
